/**
 * useUserRole — liest die Rolle des eingeloggten Users aus `public.users`.
 *
 * Der Auth-State (useAuthOrSession) liefert nur id + email — die Rolle
 * (mandant / berater / admin) liegt in der Anwendungs-Tabelle und wird
 * über RLS-geschützten SELECT abgerufen.
 *
 * Im Dev-Mock-Pfad gibt es keine Tabelle; wir leiten die Rolle aus der
 * E-Mail-Adresse ab:
 *   - "admin@*" oder "*+admin@*"  → admin
 *   - "berater@*" oder "*+berater@*" → berater
 *   - sonst → mandant
 *
 * Damit kann jeder Entwickler lokal als Admin testen, ohne Supabase-Stub.
 * Production-Pfad geht durch DB.
 *
 * Sicherheits-Hinweis: dieser Hook ist KEIN Sicherheits-Gate — die Wahrheit
 * liegt im Server-RLS. UI-Gating via Rolle verhindert nur Unsicht­bar­machen
 * von Navigation; der DB-Insert/Update würde von RLS abgelehnt.
 */

import { useEffect, useState } from 'react';

import { useAuthOrSession } from './authOrSession';
import { getProfile } from './supabaseUsers';
import type { UserRole } from './supabaseTypes';

export interface UseUserRoleResult {
  readonly role: UserRole | null;
  readonly isLoading: boolean;
  readonly isAdmin: boolean;
}

/**
 * Pure helper: leitet im Dev-Mock-Modus eine Rolle aus der E-Mail ab.
 *
 * Match-Patterns (auf den lokalen Teil vor dem @ angewandt, case-insensitive):
 *   - lokal === 'admin'             → admin
 *   - lokal === 'berater'           → berater
 *   - lokal startet mit 'admin+'    → admin   (Plus-Tag: `admin+test@…`)
 *   - lokal startet mit 'berater+'  → berater (Plus-Tag: `berater+test@…`)
 *   - lokal endet auf '+admin'      → admin   (Suffix-Tag: `christian+admin@…`)
 *   - lokal endet auf '+berater'    → berater (Suffix-Tag: `alice+berater@…`)
 *   - sonst                         → mandant
 *
 * Exportiert für Tests.
 */
export function deriveRoleFromEmail(email: string | null | undefined): UserRole {
  if (!email) return 'mandant';
  const atIdx = email.indexOf('@');
  if (atIdx <= 0) return 'mandant';
  const local = email.slice(0, atIdx).toLowerCase();

  if (local === 'admin' || local.startsWith('admin+') || local.endsWith('+admin')) {
    return 'admin';
  }
  if (local === 'berater' || local.startsWith('berater+') || local.endsWith('+berater')) {
    return 'berater';
  }
  return 'mandant';
}

/**
 * Session-Cache: pro User-ID wird das Profil-Result für die laufende
 * Browser-Session gemerkt. Verhindert, dass mehrere parallele
 * `useUserRole()`-Aufrufe (z.B. AppFrame + RequireAdmin) inkonsistente
 * Ergebnisse liefern, wenn einer der getProfile()-Calls fehlschlägt.
 *
 * sessionStorage statt localStorage: löscht sich beim Tab-Schließen,
 * keine stale-Role nach Account-Wechsel.
 */
const CACHE_KEY_PREFIX = 'tf:userRole:';

function cacheGet(userId: string): UserRole | null {
  try {
    const v = sessionStorage.getItem(CACHE_KEY_PREFIX + userId);
    if (v === 'admin' || v === 'berater' || v === 'mandant') return v;
    return null;
  } catch {
    return null;
  }
}

function cacheSet(userId: string, role: UserRole): void {
  try {
    sessionStorage.setItem(CACHE_KEY_PREFIX + userId, role);
  } catch {
    // ignore (privacy mode etc.)
  }
}

function cacheClear(): void {
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(CACHE_KEY_PREFIX)) sessionStorage.removeItem(key);
    }
  } catch {
    // ignore
  }
}

/**
 * Test-only / Logout: räumt den Profile-Cache auf.
 */
export function clearUserRoleCache(): void {
  cacheClear();
}

export function useUserRole(): UseUserRoleResult {
  const { user, isMock, isLoading: authLoading } = useAuthOrSession();

  // Initialer State: wenn der Cache schon eine Rolle für DIESEN User hat,
  // direkt verwenden — kein Flicker.
  const cached = user ? cacheGet(user.id) : null;
  const [role, setRole] = useState<UserRole | null>(cached);

  // resolvedForUserId trackt, FÜR WELCHEN User die aktuelle Rolle gilt.
  // Solange resolvedForUserId !== user.id, ist die Rolle „nicht für diesen
  // User aufgelöst" → isLoading=true. Das verhindert den Race, in dem
  // RequireAdmin zwischen „user noch null" und „Effect hat user.id gesehen"
  // einen Render mit role=null sieht und fälschlich redirected.
  const [resolvedForUserId, setResolvedForUserId] = useState<string | null>(
    cached !== null && user ? user.id : null,
  );

  useEffect(() => {
    let cancelled = false;

    if (!user) {
      setRole(null);
      setResolvedForUserId(null);
      return;
    }

    if (isMock) {
      // Dev-Mock: Rolle aus E-Mail ableiten — kein DB-Call.
      const r = deriveRoleFromEmail(user.email);
      cacheSet(user.id, r);
      setRole(r);
      setResolvedForUserId(user.id);
      return;
    }

    // Production: cache-first
    const c = cacheGet(user.id);
    if (c !== null) {
      setRole(c);
      setResolvedForUserId(user.id);
      return;
    }

    void getProfile()
      .then((profile) => {
        if (cancelled) return;
        const r = profile?.role ?? 'mandant';
        cacheSet(user.id, r);
        setRole(r);
        setResolvedForUserId(user.id);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.warn('useUserRole: getProfile failed', err);
        // KEIN cacheSet im Fehlerfall — beim nächsten Hook-Mount nochmal versuchen.
        setRole('mandant');
        setResolvedForUserId(user.id);
      });

    return () => {
      cancelled = true;
    };
  }, [user?.id, user, isMock]);

  // Derived isLoading — nicht eigener State, weil der State immer dem Render
  // hinterherhinkt. So ist der Wert konsistent mit user/resolvedForUserId.
  const isLoading = authLoading || (user !== null && resolvedForUserId !== user.id);

  return {
    role,
    isLoading,
    // isAdmin nur true, wenn die Rolle WIRKLICH für den aktuellen User gilt.
    // Verhindert stale-admin nach User-Wechsel.
    isAdmin: role === 'admin' && user !== null && resolvedForUserId === user.id,
  };
}
